import {faPlus, faArrowCircleRight} from '@fortawesome/free-solid-svg-icons'
import {FontAwesomeIcon} from '@fortawesome/react-fontawesome'
import sortBy from 'lodash/sortBy'
import type {LangT} from '@freckle/educator-entities/ts/common/helpers/languages'
import {useAsync} from '@freckle/educator-entities/ts/common/hooks/use-async'
import type {PremiumLicensesAttrs} from '@freckle/educator-entities/ts/common/models/premium-licenses'
import {fetchMathFactPracticeOperationsForStudent} from '@freckle/educator-entities/ts/math/fact-practice/api/index'
import type {FactPracticeOperationType} from '@freckle/educator-entities/ts/math/fact-practice/helpers/fact-practice-operation'
import {FactPracticeOperations} from '@freckle/educator-entities/ts/math/fact-practice/helpers/fact-practice-operation'
import {getMaximumLevelForFactPracticeOperation} from '@freckle/educator-entities/ts/math/fact-practice/logic/fact-practice'
import type {
  FactPracticeCourseLevelsAttrs,
  FactPracticeLevelsAttr,
} from '@freckle/educator-entities/ts/math/fact-practice/models/math-fact-practice-course-levels'
import {formattedName, nameForSorting} from '@freckle/educator-entities/ts/users/models/student'
import type {StudentAttrs} from '@freckle/educator-entities/ts/users/models/student'
import {Link} from '@freckle/educator-materials/ts/components/link'
import {useSafeTranslation} from '@freckle/educator-materials/ts/helpers/translate'
import {NoDataNotice} from '@freckle/classroom/ts/common/components/no-data-notice'
import {PremiumPopover} from '@freckle/classroom/ts/common/components/premium-popover-legacy'
import {PremiumFeatures} from '@freckle/classroom/ts/common/logic/premium-features'
import type {FactPracticeReportTabNameT} from '@freckle/classroom/ts/common/routers/routes'
import {Routes} from '@freckle/classroom/ts/common/routers/routes'
import {useCurrentCourseId} from '@freckle/classroom/ts/hooks/use-current-course-id'
import {FactPracticeLevelTooltip} from './fact-practice-level-tooltip'
import {FactPacticeReportInfo} from './fact-practice-report-info'
import {
  noticeBarReportDescription,
  reportLabel,
  reportDescription,
} from '../../../../common/components/no-data-notice.module.scss'
import {alertNoDataStyle} from './fact-mastery-summary.module.scss'

type FactMasterySummaryProps = {
  students: Array<StudentAttrs>
  mathFactPracticeCourseLevels: Array<FactPracticeCourseLevelsAttrs>
  isPremium: boolean
  premiumLicenses: PremiumLicensesAttrs
  language: LangT
  navigateFactPracticeAssign: () => void
  activeTab: FactPracticeReportTabNameT
}

export function FactMasterySummary(props: FactMasterySummaryProps) {
  const {mathFactPracticeCourseLevels, language, activeTab} = props
  const {t} = useSafeTranslation()
  if (activeTab === 'activity') return <></>
  const operationHeaders = FactPracticeOperations.operationsByType(
    activeTab as FactPracticeOperationType,
  ).map(operation => {
    const formattedOperation = t('REPORT_FACT_PRACTICE_OPERATIONS_LABEL_V2', {operation})
    const prefix = t('FACT_MASTERY_SUMMARY_LEVEL_TOOLTIP_CONTENT_PREFIX', {
      operation: formattedOperation,
      maxLevel: getMaximumLevelForFactPracticeOperation(operation),
    })

    return (
      <th key={operation}>
        <FactPracticeLevelTooltip tooltipPrefix={prefix}>
          <>{formattedOperation}</>
        </FactPracticeLevelTooltip>
      </th>
    )
  })

  const sortedStudents = sortBy(props.students, nameForSorting) as StudentAttrs[]
  const rows = sortedStudents.map(student => {
    const levelsForStudent =
      mathFactPracticeCourseLevels.find(allLevels => allLevels.studentId === student.id)?.levels ??
      []

    return (
      <FactMasterySummaryRow
        key={student.id}
        student={student}
        mathFactPracticeCourseLevelsForStudent={levelsForStudent}
        teacherLanguage={language}
        activeTab={activeTab as FactPracticeOperationType}
      />
    )
  })

  const [limitedRows, premiumMessage] =
    props.isPremium || rows.length <= 3
      ? [rows, null]
      : [
          rows.slice(0, 3),
          <PremiumPopover
            key="1"
            feature={PremiumFeatures.Math}
            featureDescription="Fact Mastery Summary"
            premiumLicenses={props.premiumLicenses}
          >
            <div className="row std-margin-top-sm">
              <div className="col-12">
                <div className="alert alert-info text-center bold">
                  <p className="link-cursor">{t('FACT_MASTERY_SUMMARY_SEE_ALL_STUDENTS')}</p>
                </div>
              </div>
            </div>
          </PremiumPopover>,
        ]

  const assignFactPractice = (
    <span>
      <FontAwesomeIcon icon={faPlus} /> {t('FACT_MASTERY_SUMMARY_ASSIGN_PRACTICE')}
    </span>
  )

  const alertNoData = (
    <NoDataNotice
      subject="math"
      imageName="math/fact-practice/empty-mastery-report"
      onButtonClick={props.navigateFactPracticeAssign}
      noticeBarLeftElement={
        <div className={noticeBarReportDescription}>
          <span className={reportLabel}>{t('FACT_MASTERY_SUMMARY_NO_DATA_NOTICE_LABEL')}</span>
          <span className={reportDescription}>
            {t('FACT_MASTERY_SUMMARY_NO_DATA_NOTICE_DESCRIPTION')}
          </span>
        </div>
      }
      noticeBarRightButtonBody={assignFactPractice}
      textToOverlay={t('FACT_MASTERY_SUMMARY_TABLE_NO_DATA_OVERLAY_MESSAGE')}
    />
  )

  return rows.length === 0 ? (
    <div className={alertNoDataStyle}>{alertNoData}</div>
  ) : (
    <div className="row">
      <div className="col-12">
        <FactPacticeReportInfo />

        <div className="row fact-mastery-summary">
          <div className="col-12">
            <table className="fact-mastery-summary-table">
              <thead>
                <tr>
                  <th key="student">{t('FACT_MASTERY_SUMMARY_TABLE_HEADER_STUDENT')}</th>
                  {operationHeaders}
                  <th>{t('FACT_MASTERY_SUMMARY_TABLE_HEADER_REPORT')}</th>
                </tr>
              </thead>
              <tbody>{limitedRows}</tbody>
            </table>
          </div>
        </div>
        {premiumMessage}
      </div>
    </div>
  )
}

type FactMasterySummaryRowProps = {
  student: StudentAttrs
  mathFactPracticeCourseLevelsForStudent: Array<FactPracticeLevelsAttr>
  teacherLanguage: LangT
  activeTab: FactPracticeOperationType
}

function FactMasterySummaryRow(props: FactMasterySummaryRowProps) {
  const {mathFactPracticeCourseLevelsForStudent, student, teacherLanguage, activeTab} = props
  const courseId = useCurrentCourseId()
  const {t} = useSafeTranslation()

  const operationsAvailableForStudent =
    useAsync(
      () =>
        fetchMathFactPracticeOperationsForStudent(
          courseId,
          student.id,
          student.grade,
          teacherLanguage,
        ),
      'fetchMathFactPracticeOperationsForStudent',
      [courseId, student.id, student.grade, teacherLanguage],
    ).data ?? []

  const levelCells = FactPracticeOperations.operationsByType(activeTab).map(operation => {
    const operationAvailableForStudent = operationsAvailableForStudent.some(
      availableOp => availableOp.operation === operation,
    )
    const mLevel = mathFactPracticeCourseLevelsForStudent.find(
      allOpLevelsForStudent => allOpLevelsForStudent.operation === operation,
    )?.level

    const levelString =
      operationAvailableForStudent && mLevel !== undefined
        ? mLevel
        : t('FACT_MASTERY_SUMMARY_TABLE_CELL_VALUE_NA')
    return (
      <td key={operation}>
        <small>{t('FACT_MASTERY_SUMMARY_TABLE_CELL_LABEL_LEVEL')}</small>
        <p>{levelString}</p>
      </td>
    )
  })

  return (
    <tr>
      <td>{formattedName(props.student)}</td>
      {levelCells}
      <td className="fact-mastery-summary-icon">
        <Link
          href={Routes.math.factPracticeReport({
            courseId,
            studentId: props.student.id,
            operation: FactPracticeOperations.operationsByType(activeTab)[0]!,
          })}
          reactRouter
        >
          <FontAwesomeIcon icon={faArrowCircleRight} size="lg" />
        </Link>
      </td>
    </tr>
  )
}
